/* eslint-disable max-lines-per-function */

/* eslint-disable complexity */
import _ from 'lodash';
import moment from 'moment';
import {
  BILLION,
  BY_DEFAULT_CARDS_DESKTOP,
  BY_DEFAULT_CARDS_TABLET,
  CRORE,
  DIVIDED_BY_DESKTOP,
  DIVIDED_BY_TABLET,
  LAKH,
  MILLION,
  THOUSAND,
} from './constant';
import {
  CardViewData,
  GetCardViewDataParams,
  ProcessedCardData,
} from './types';

// Helper function from utils
const isDecimalNumber = (number: number): boolean => number % 1 !== 0;

// If convertToKMBOrKLC is not available, use this implementation
// Otherwise, use the imported one
const convertToKLC = (
  input: number | string,
  decimal: number = 0,
  locale: string = 'en-IN',
): string => {
  let number = parseFloat(String(input));
  const numberFormatter = new Intl.NumberFormat(locale);
  const signString = number < 0 ? '-' : '';

  if (number >= CRORE) {
    number /= CRORE;
    return `${signString}${numberFormatter.format(
      Number(number.toFixed(decimal)),
    )}C`;
  }
  if (number >= LAKH) {
    number /= LAKH;
    return `${signString}${numberFormatter.format(
      Number(number.toFixed(decimal)),
    )}L`;
  }
  if (number >= THOUSAND) {
    number /= THOUSAND;
    return `${signString}${numberFormatter.format(
      Number(number.toFixed(decimal)),
    )}K`;
  }
  return isDecimalNumber(number)
    ? `${signString}${numberFormatter.format(Number(number.toFixed(decimal)))}`
    : `${signString}${numberFormatter.format(number)}`;
};

// TODO: Replace with actual web implementation that uses your translation system
const convertToKMB = (
  input: number | string,
  decimal: number = 0,
  locale: string = 'en-IN',
): string => {
  let number = parseFloat(String(input));
  const signString = number < 0 ? '-' : '';
  const numberFormatter = new Intl.NumberFormat(locale);
  if (number >= BILLION) {
    number /= BILLION;
    return `${signString}${numberFormatter.format(
      Number(number.toFixed(decimal)),
    )}B`;
  }
  if (number >= MILLION) {
    number /= MILLION;
    return `${signString}${numberFormatter.format(
      Number(number.toFixed(decimal)),
    )}M`;
  }
  if (number >= THOUSAND) {
    number /= THOUSAND;
    return `${signString}${numberFormatter.format(
      Number(number.toFixed(decimal)),
    )}K`;
  }
  return isDecimalNumber(number)
    ? `${signString}${numberFormatter.format(Number(number.toFixed(decimal)))}`
    : `${signString}${numberFormatter.format(number)}`;
};

// Web-adapted convertToKMBOrKLC function
// TODO: Replace ConfigStore with your web config service
export const convertToKMBOrKLCWeb = (
  input: number | string,
  decimal: number = 0,
  showCurrency: boolean = false,
  showPercentage: boolean = false,
  separator: string = '',
  // Web config - replace with your actual config service
  config?: {
    currencyQuantity?: string;
    currency?: string;
    locale?: string;
  },
): string => {
  // Default config if not provided
  const currencyQuantity = config?.currencyQuantity || 'KLC';
  const currency = config?.currency || '';
  const locale = config?.locale || 'en-IN';

  let value: string | number = input;
  const numValue = Number(input);
  if (typeof numValue === 'number' && numValue > 0) {
    if (currencyQuantity === 'KMB' || currencyQuantity === 'KJTM') {
      value = convertToKMB(input, decimal, locale);
    } else {
      value = convertToKLC(input, decimal, locale);
    }
  }

  // Trim 0's at end
  if (typeof value === 'string') {
    const [val, float] = value.split('.');
    if (!float) {
      value = val;
    } else {
      const decimalValue = float?.slice(0, float.length - 1);
      if (decimalValue === '0'.repeat(decimal)) {
        value = `${val}${float.charAt(float.length - 1)}`;
      } else {
        value = `${val}.${float}`;
      }
    }
  }

  if (showCurrency && currency) {
    return `${currency}${separator}${value}`;
  }

  if (showPercentage) {
    return `${value}%`;
  }

  return String(value);
};

// Use imported function if available, otherwise use web version
const getConvertToKMBOrKLC =
  (config?: any) =>
  // Try to use imported mobile function, fallback to web implementation
  (
    input: number | string,
    decimal: number = 0,
    showCurrency: boolean = false,
    showPercentage: boolean = false,
    separator: string = '',
  ) =>
    convertToKMBOrKLCWeb(
      input,
      decimal,
      showCurrency,
      showPercentage,
      separator,
      config,
    );
// TODO: Import getFieldOptionsNameFromChipFilters from mobile keyMetricsHelper or create web equivalent
// This function is used for frequency text
export const getFieldOptionsNameFromChipFilters = (
  chipFilter: any = {},
  frequency: string = '',
): string => {
  // TODO: Import types from your web project
  // const types = require('your-web-input-types');
  const SIFG_TYPE = 'SIFG'; // Replace with actual type constant

  let codeNameSpinnerOptions: any[] = [];
  if (chipFilter?.type === SIFG_TYPE) {
    codeNameSpinnerOptions =
      chipFilter.sifg_options?.selection?.code_name_spinner_options || [];
  } else {
    codeNameSpinnerOptions = chipFilter?.codeNameSpinnerOptions || [];
  }
  const matchingOption = codeNameSpinnerOptions?.find(
    (options) => options.code === frequency,
  );
  return matchingOption?.name || '';
};

const getMetricActualExpectedCount = (
  metric: any,
  showCurrency: boolean,
  showPercentage: boolean,
  convertFn: (
    input: number | string,
    decimal?: number,
    showCurrency?: boolean,
    showPercentage?: boolean,
    separator?: string,
  ) => string,
) => {
  const targetValue = metric?.target?.value;
  const achievementValue = metric?.value ?? 0;
  let targetText = '';
  const achievementText =
    metric?.achievement_assigned === false
      ? '-'
      : convertFn(achievementValue, 2, showCurrency, showPercentage);
  if (_.isNumber(targetValue)) {
    targetText = ` / ${convertFn(
      targetValue,
      2,
      showCurrency,
      showPercentage,
    )}`;
  }
  return `${achievementText}${targetText}`;
};

export const getCardViewData = (
  card: GetCardViewDataParams = {},
  cardsFrequency?: string,
  chipFilter: any = {},
  // Web config - replace with your actual config service
  config?: {
    currencyQuantity?: string;
    currency?: string;
    locale?: string;
  },
): CardViewData => {
  let actualExpectedCount = ' / ';
  let pbMax = 0;
  let pbFillProgress = 0;
  let pbFillColor = '#8590a2'; // Default color - MENU_BORDER equivalent
  let progressContainerAttributes: any[] = [];
  let lastUpdatedBy = '';
  let expectedProgress = 0;
  let errorText = '';
  let expectedProgressText = '';
  let reportBreakdownName = '';

  // Get the conversion function
  const convertFn = getConvertToKMBOrKLC(config);

  // Get frequency text if needed
  const metricFrequency =
    card.context?.metricsData?.result?.metric?.frequency || '';
  const frequencyText =
    cardsFrequency === 'all' && card.context?.metricsData?.result?.metric
      ? getFieldOptionsNameFromChipFilters(chipFilter, metricFrequency)
      : '';

  if (card.context?.metricsData?.result?.metric) {
    const { metric } = card.context.metricsData.result;
    const showCurrency = metric.data_type === 'currency';
    const showPercentage = metric.data_type === 'percentage';
    expectedProgress = metric.target?.expected_progress;
    expectedProgressText = convertFn(
      expectedProgress,
      2,
      showCurrency,
      showPercentage,
    );
    pbMax = metric.target?.value || 0;
    pbFillProgress = !(pbMax === 0 && metric.value > 0)
      ? metric.value / pbMax || 0
      : 0;
    actualExpectedCount = getMetricActualExpectedCount(
      metric,
      showCurrency,
      showPercentage,
      convertFn,
    );

    const progressType = metric.target?.progress_type;
    const colorConfig = card.context.metricsData?.progress_type_settings?.find(
      (it) => it.goal_progress_type === progressType,
    );
    if (colorConfig) {
      pbFillColor = colorConfig.color;
    }

    if (metric.target?.attributes?.length) {
      let index = 0;
      progressContainerAttributes = [{}, {}, {}];
      metric.target?.attributes?.slice(0, 3)?.forEach((attribute: any) => {
        progressContainerAttributes[index] = attribute;
        index += 1;
      });
    }

    // Date formatting with moment.js
    if (metric.last_updated_date) {
      const locale = config?.locale || 'en';
      lastUpdatedBy = `Last updated on ${moment(metric.last_updated_date)
        .locale(locale)
        .format('DD MMM YYYY')}`;
    }

    reportBreakdownName = card.context?.metricsData?.result?.report || '';
  }

  if (card.context?.metricsData?.errorMessage) {
    errorText = card.context.metricsData.errorMessage;
  }

  return {
    actualExpectedCount,
    frequencyText,
    pbFillProgress,
    pbFillColor,
    progressContainerAttributes,
    lastUpdatedBy,
    expectedProgress,
    pbMax,
    errorText,
    expectedProgressText,
    reportBreakdownName,
    showProgressBar: pbMax > 0,
  };
};

// Calculate container width for cards
export const getContainerWidth = (
  noOfCardsToShow: number,
  containerWidth: number,
): number => {
  const byDefaultCards =
    containerWidth > 1000 ? BY_DEFAULT_CARDS_DESKTOP : BY_DEFAULT_CARDS_TABLET;
  const dividingBy =
    containerWidth > 1000 ? DIVIDED_BY_DESKTOP : DIVIDED_BY_TABLET;
  return noOfCardsToShow > byDefaultCards
    ? containerWidth / dividingBy
    : containerWidth / noOfCardsToShow;
};

export const processCardData = (
  snapshotCard: {
    code: string;
    name?: string;
    meta_data?: {
      goal_name?: string;
      frequency?: string;
    };
    data?: {
      status?: string;
      body?: any;
    };
  },
  metricsData?: Array<{
    code: string;
    name?: string;
    meta_data?: {
      goal_name?: string;
      frequency?: string;
    };
    data?: {
      status?: string;
      body?: any;
    };
  }>,
  apiFailure?: (errorMsg?: string) => void,
): ProcessedCardData => {
  // snapshotCard already has the combined data structure from selector
  if (snapshotCard.data) {
    const result = snapshotCard.data;
    if (result.status === 'SUCCESS') {
      const snapshotCardData = result.body || {};
      const cardTitle =
        snapshotCard.name ||
        snapshotCard.meta_data?.goal_name ||
        snapshotCard.code;

      return {
        cardData: {
          type: 'metric',
          title: cardTitle,
          show_snapshot_card: true,
          result: snapshotCardData,
        },
        loading: false,
      };
    }
    if (result.status === 'FAILED' && apiFailure) {
      apiFailure();
      return {
        loading: false,
        error: 'Failed to load performance metrics data',
      };
    }
  }

  // If no data or failed, don't show the card
  return {
    loading: false,
  };
};
